// Local design tokens for @funkit/connect-rn, generated from the "Fun Variables"
// Figma export (`Fun Variables/{Light,Dark}.tokens.json`). This file is a STRICT
// mirror of that export: the token tree reproduces Figma's variable HIERARCHY
// one-to-one (`background` / `content` / `border` / `font` / `spacing` / `brand`
// / `button` / `opacity`), values are the resolved concrete values, and nothing
// is invented — if a state (hover, focused, …) or token isn't in Fun Variables,
// it isn't here either.
//
// Multi-word Figma leaves are camelCased so they're valid TS identifiers
// (`element-inner` → `elementInner`, `loading alpha` → `loadingAlpha`,
// `amount input` → `amountInput`, `top bar` → `topBar`); the nesting is
// otherwise identical to the JSON.
//
// `buildFunkitTheme` (buildFunkitTheme.ts) FLATTENS this tree into the flat
// `@shopify/restyle` theme components consume (`bgButtonPrimary`, …). The
// Figma-path → flat-key convention is documented in
// `packages/connect-rn/CLAUDE.md` (§ "Figma Fun Variables → tokens.ts").
//
// connect-rn's theme is intentionally NOT shared with @funkit/connect-core (web
// keeps its separate `ThemeVars`).

import type { TextStyle } from 'react-native'

/**
 * A Figma font-style name (`font/…/weight`). These are the only cuts the design
 * uses, so a literal `fontWeight` in a component is a smell that should route
 * through the token instead.
 */
export type FunkitFontStyleName = 'Regular' | 'Medium' | 'SemiBold'

/** Logical weight a component's `Text` can request; maps onto `font/body/weight/*`. */
export type FunkitFontWeight = 'base' | 'medium' | 'semibold'

/** `font/*` — body and header typography. Family is shared by both in Light/Dark. */
export interface FunkitFontTokens {
  body: {
    /**
     * `font/body/family`. The Fun Variables value is `Inter`, so the host must
     * register `Inter-Regular` / `Inter-Medium` (see {@link fontFamilyForWeight});
     * if it doesn't, RN falls back to the platform system font.
     */
    family: string
    weight: Record<FunkitFontWeight, FunkitFontStyleName>
    size: { base: number; detail: number }
    // Absolute px (RN's TextStyle.lineHeight).
    leading: { base: number; detail: number }
  }
  header: {
    family: string
    weight: FunkitFontStyleName
    alignment: 'Left' | 'Center' | 'Right'
    // `amount input` / `amount summary` are single-line; Figma defines no leading.
    size: {
      title: number
      subtitle: number
      // The left-aligned heading a disclosure drawer opens with.
      drawerTitle: number
      amountInput: number
      amountSummary: number
    }
  }
}

/** One button state's fill (`background/button/*` — only `default` + `pressed`). */
export interface ButtonSurface {
  default: string
  pressed: string
}

/**
 * The full token set for one mode — a one-to-one mirror of the Fun Variables
 * Figma variable groups.
 */
export interface FunkitTokens {
  background: {
    modal: string
    secondary: string
    button: {
      primary: ButtonSurface
      secondary: ButtonSurface
      tertiary: ButtonSurface
      icon: ButtonSurface
      destructive: ButtonSurface & { disabled: string }
    }
    pill: { default: string; pressed: string; disabled: string }
    /**
     * Overlay dimming the host app behind the flow sheet. NOT yet a Fun Variable
     * — the design (13315:9391) hardcodes it — so it lives here to stay
     * per-client overridable. Fold it in when the export gains the variable.
     */
    overlay: string
  }
  /** `opacity/disabled` — a percentage (0–100); divide by 100 for RN `opacity`. */
  opacity: {
    disabled: number
  }
  content: {
    elements: {
      primary: string
      secondary: string
      success: string
      error: string
      hyperlink: string
      warning: string
      /** `Content/Watermark` — the SDK attribution, alpha baked in. */
      watermark: string
      /** `content/tertiary` — a placeholder, and the caption under one. */
      tertiary: string
      /** `content/ghosted` — the disc a back chevron sits on. */
      ghosted: string
    }
    // Label/icon color on a button surface. `loadingAlpha` (`loading alpha`) is
    // the same color at reduced alpha, shown while the button is loading.
    button: {
      primary: { default: string; loadingAlpha: string }
      secondary: { default: string; loadingAlpha: string }
      tertiary: { default: string }
      icon: { default: string }
      destructive: { default: string; loadingAlpha: string }
    }
  }
  border: {
    radius: {
      modal: number
      element: number
      elementInner: number
      button: number
    }
    modal: { default: string; weight: number }
    elements: {
      default: { color: string; weight: number }
      selected: { color: string; weight: number }
    }
    button: { primary: string; secondary: string; weight: number }
  }
  /**
   * `form/*` — the filled boxes a form is drawn as.
   *
   * Local to the KYC design file, NOT yet Fun Variables, so these live here to
   * stay per-client overridable (as `background/overlay` does). Fold them into
   * the mirror when the export gains them.
   */
  form: {
    /** `form/field` — the field fill. */
    field: string
    /** `palette/palegrey` — the code box the next digit lands in. */
    fieldNext: string
    /** The rule between two rows of a grouped card. No variable names it. */
    divider: string
    /**
     * A field whose answer is refused, and the round's own error, which is
     * drawn as a field-sized banner (259:12593).
     *
     * The design keeps one pale yellow across both modes, so what sits on it
     * is dark in both rather than following `content/primary`.
     */
    fieldInvalid: { fill: string; content: string; placeholder: string }
    /**
     * The round's own error, drawn as a tinted notice under the fields
     * (1898:3254). A tenth of its own gold, so it reads as a note rather than
     * as another refused field.
     */
    errorNotice: { fill: string; content: string }
  }
  font: FunkitFontTokens
  spacing: {
    padding: {
      elements: { horizontal: number }
      modal: { horizontal: number }
    }
    heights: { topBar: number; button: number }
  }
  brand: {
    primary: string
  }
  button: {
    // Icon tiers collapse per the export: xs === sm (16) and md === lg (20).
    // The four tiers are kept so call sites stay semantic.
    icon: {
      size: { xs: number; sm: number; md: number; lg: number }
    }
    size: { xs: number; sm: number; md: number; lg: number }
  }
}

/**
 * Resolves a font family + Figma style name to the concrete native font-family
 * name the host must register — e.g. `('Inter', 'Medium')` → `'Inter-Medium'`.
 *
 * This is connect-rn's whole answer to a React Native / Expo quirk: you cannot
 * reliably select a custom font's weight via `fontWeight`. Android matches custom
 * fonts by exact family-name string, and a font loaded at runtime (`expo-font`)
 * registers one native Typeface per name — so `fontWeight: '500'` on
 * `fontFamily: 'Inter'` is silently ignored and every weight renders identically.
 * Weight therefore has to be baked into the family name, and every text style
 * goes through here.
 */
export function fontFamilyForWeight(
  family: string,
  styleName: FunkitFontStyleName,
): string {
  if (family === 'System') {
    return family
  }
  return `${family}-${styleName}`
}

/** Figma font-style name → CSS numeric weight (for the Swapped WebView / RN `fontWeight`). */
export const FONT_STYLE_CSS_WEIGHT: Record<
  FunkitFontStyleName,
  TextStyle['fontWeight']
> = {
  Regular: '400',
  Medium: '500',
  SemiBold: '600',
}

export const lightTokens: FunkitTokens = {
  background: {
    modal: '#FFFFFF',
    secondary: '#F9F9F9',
    button: {
      primary: { default: '#0F0F0F', pressed: '#1B1B1B' },
      secondary: { default: '#F9F9F9', pressed: '#DDDDDD' },
      tertiary: { default: '#FFFFFF', pressed: '#EFEFEF' },
      icon: { default: '#FFFFFF', pressed: '#EFEFEF' },
      destructive: {
        default: '#F34126',
        pressed: '#C2341E',
        disabled: '#F34126',
      },
    },
    pill: { default: '#FFFFFF', pressed: '#EFEFEF', disabled: '#FFFFFF' },
    overlay: '#131313CC',
  },
  opacity: {
    disabled: 40,
  },
  content: {
    elements: {
      primary: '#0F0F0F',
      secondary: '#6C6C6C',
      success: '#52A300',
      error: '#F34126',
      hyperlink: '#0F0F0F',
      warning: '#F97316',
      watermark: '#0F0F0F29',
      tertiary: '#00000033',
      ghosted: '#0000001A',
    },
    button: {
      primary: { default: '#FFFFFF', loadingAlpha: '#FFFFFF4D' },
      secondary: { default: '#0F0F0F', loadingAlpha: '#0000004D' },
      tertiary: { default: '#0F0F0F' },
      icon: { default: '#6C6C6C' },
      destructive: { default: '#FFFFFF', loadingAlpha: '#FFFFFF80' },
    },
  },
  form: {
    field: '#E9EBEF',
    fieldNext: '#DDDEDE',
    divider: '#D1D3D7',
    fieldInvalid: {
      fill: '#F6E48C',
      content: '#0F0F0F',
      placeholder: '#0F0F0F99',
    },
    errorNotice: { fill: '#BAA2631A', content: '#AE9B3D' },
  },
  border: {
    radius: { modal: 24, element: 12, elementInner: 8, button: 999 },
    modal: { default: '#FFFFFF', weight: 1 },
    elements: {
      default: { color: '#EFEFEF', weight: 1 },
      selected: { color: '#0F0F0F', weight: 1 },
    },
    button: { primary: '#0F0F0F', secondary: '#F9F9F9', weight: 0 },
  },
  font: {
    body: {
      family: 'Inter',
      weight: { base: 'Regular', medium: 'Medium', semibold: 'SemiBold' },
      size: { base: 16, detail: 14 },
      leading: { base: 22, detail: 20 },
    },
    header: {
      family: 'Inter',
      weight: 'Medium',
      alignment: 'Center',
      size: {
        title: 18,
        subtitle: 16,
        drawerTitle: 24,
        amountInput: 48,
        amountSummary: 48,
      },
    },
  },
  spacing: {
    padding: {
      elements: { horizontal: 12 },
      modal: { horizontal: 16 },
    },
    heights: { topBar: 56, button: 42 },
  },
  brand: {
    primary: '#0F0F0F',
  },
  button: {
    icon: { size: { xs: 16, sm: 16, md: 20, lg: 20 } },
    size: { xs: 36, sm: 40, md: 48, lg: 56 },
  },
}

export const darkTokens: FunkitTokens = {
  background: {
    modal: '#0F0F0F',
    secondary: '#1B1B1B',
    button: {
      primary: { default: '#FFFFFF', pressed: '#F9F9F9' },
      secondary: { default: '#1B1B1B', pressed: '#2D2D2D' },
      tertiary: { default: '#1B1B1B', pressed: '#2D2D2D' },
      icon: { default: '#0F0F0F', pressed: '#1B1B1B' },
      destructive: {
        default: '#DB3B22',
        pressed: '#F34126',
        disabled: '#DB3B22',
      },
    },
    pill: { default: '#0F0F0F', pressed: '#1B1B1B', disabled: '#0F0F0F' },
    overlay: '#131313CC',
  },
  opacity: {
    disabled: 40,
  },
  content: {
    elements: {
      primary: '#FFFFFF',
      secondary: '#929292',
      success: '#52A300',
      error: '#F34126',
      hyperlink: '#FFFFFF',
      warning: '#F97316',
      watermark: '#FFFFFF29',
      tertiary: '#FFFFFF33',
      ghosted: '#FFFFFF1A',
    },
    button: {
      primary: { default: '#000000', loadingAlpha: '#0000004D' },
      secondary: { default: '#F9F9F9', loadingAlpha: '#FFFFFF4D' },
      tertiary: { default: '#F9F9F9' },
      icon: { default: '#929292' },
      destructive: { default: '#FFFFFF', loadingAlpha: '#FFFFFF80' },
    },
  },
  // Derived, not drawn: the design ships Light only. Same relationship to the
  // surface as Light, inverted — a hair above the dark modal fill.
  form: {
    field: '#1F1F1F',
    fieldNext: '#2C2C2C',
    divider: '#373737',
    fieldInvalid: {
      fill: '#F6E48C',
      content: '#0F0F0F',
      placeholder: '#0F0F0F99',
    },
    errorNotice: { fill: '#BAA2631A', content: '#AE9B3D' },
  },
  border: {
    radius: { modal: 24, element: 12, elementInner: 8, button: 999 },
    modal: { default: '#1B1B1B', weight: 1 },
    elements: {
      default: { color: '#2D2D2D', weight: 1 },
      selected: { color: '#FFFFFF', weight: 1 },
    },
    button: { primary: '#FFFFFF', secondary: '#1B1B1B', weight: 0 },
  },
  font: {
    body: {
      family: 'Inter',
      weight: { base: 'Regular', medium: 'Medium', semibold: 'SemiBold' },
      size: { base: 16, detail: 14 },
      leading: { base: 22, detail: 20 },
    },
    header: {
      family: 'Inter',
      weight: 'Medium',
      alignment: 'Center',
      size: {
        title: 18,
        subtitle: 16,
        drawerTitle: 24,
        amountInput: 48,
        amountSummary: 48,
      },
    },
  },
  spacing: {
    padding: {
      elements: { horizontal: 12 },
      modal: { horizontal: 16 },
    },
    heights: { topBar: 56, button: 42 },
  },
  brand: {
    primary: '#0F0F0F',
  },
  button: {
    icon: { size: { xs: 16, sm: 16, md: 20, lg: 20 } },
    size: { xs: 36, sm: 40, md: 48, lg: 56 },
  },
}
